export function parseConfigActionNote(note) {
    const text = String(note || '');
    const segments = text.split(' | ').map((item) => item.trim()).filter(Boolean);
    const extractQuotedField = (source, key) => {
        if (!source) return null;

        const singleQuoted = new RegExp(`'${key}'\\s*:\\s*'([^']*)'`);
        const doubleQuoted = new RegExp(`"${key}"\\s*:\\s*"([^"]*)"`);
        const numeric = new RegExp(`['"]${key}['"]\\s*:\\s*(\\d+)`);

        return source.match(singleQuoted)?.[1]
            || source.match(doubleQuoted)?.[1]
            || source.match(numeric)?.[1]
            || null;
    };

    const summary = {
        transitions: [],
        rules: [],
        msbs: [],
        severity: [],
        changedSections: [],
        recentViolations: null,
        validationResults: null,
        configSnapshots: null,
        configDiffPresent: null,
        configFileName: null,
        configChangePk: null,
        vendor: null,
        addedCount: null,
        removedCount: null,
        diffSnippet: null,
        ruleContext: null,
        msbContext: null,
        explicitValidationResults: null,
        rawModelSummary: null,
        rawModelSummaryFull: null,
        modelReason: null,
        modelRecommendedAction: null,
        modelRiskScore: null,
        modelConfidence: null,
        actionCategory: null,
    };

    const correlationSegment = segments.find((item) => item.startsWith('correlation='));
    if (correlationSegment) {
        const correlationText = correlationSegment.slice('correlation='.length);
        correlationText
            .split(';')
            .map((item) => item.trim())
            .filter(Boolean)
            .forEach((part) => {
                const [rawKey, ...rest] = part.split('=');
                const key = (rawKey || '').trim();
                const value = rest.join('=').trim();
                if (!key || !value) return;

                if (key === 'pass_to_fail' && value !== '0') summary.transitions.push(`PASS->FAIL ${value}`);
                if (key === 'pass_to_unknown' && value !== '0') summary.transitions.push(`PASS->UNKNOWN ${value}`);
                if (key === 'fail_to_unknown' && value !== '0') summary.transitions.push(`FAIL->UNKNOWN ${value}`);
                if (key === 'fail_to_pass' && value !== '0') summary.transitions.push(`FAIL->PASS ${value}`);
                if (key === 'rules') summary.rules = value.split(',').map((v) => v.trim()).filter(Boolean);
                if (key === 'msbs') summary.msbs = value.split(',').map((v) => v.trim()).filter(Boolean);
                if (key === 'severity') summary.severity = value.split(',').map((v) => v.trim()).filter(Boolean);
                if (key === 'recent_violations') summary.recentViolations = value;
                if (key === 'validation_results') summary.validationResults = value;
                if (key === 'config_snapshots') summary.configSnapshots = value;
                if (key === 'config_diff_present') summary.configDiffPresent = value;
            });
    }

    const configFileNameSegment = segments.find((item) => item.startsWith('config_file_name='));
    if (configFileNameSegment) {
        summary.configFileName = configFileNameSegment.slice('config_file_name='.length).trim();
    }

    const vendorSegment = segments.find((item) => item.startsWith('vendor='));
    if (vendorSegment) {
        summary.vendor = vendorSegment.slice('vendor='.length).trim();
    }

    const configChangeSegment = segments.find((item) => item.startsWith('config_change_pk='));
    if (configChangeSegment) {
        summary.configChangePk = configChangeSegment.slice('config_change_pk='.length).trim();
    }

    const changedSectionsSegment = segments.find((item) => item.startsWith('changed_sections='));
    if (changedSectionsSegment) {
        summary.changedSections = changedSectionsSegment
            .slice('changed_sections='.length)
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean);
    }

    const diffStatsSegment = segments.find((item) => item.startsWith('diff_stats='));
    if (diffStatsSegment) {
        const value = diffStatsSegment.slice('diff_stats='.length);
        value.split(',').forEach((part) => {
            const [key, rawValue] = part.split(':');
            if (key === 'added') summary.addedCount = rawValue;
            if (key === 'removed') summary.removedCount = rawValue;
        });
    }

    const diffSegment = segments.find((item) => item.startsWith('diff='));
    if (diffSegment) {
        const diffText = diffSegment.slice('diff='.length).replace(/\s+/g, ' ').trim();
        summary.diffSnippet = diffText.length > 260 ? `${diffText.slice(0, 260)}...` : diffText;
    }

    const ruleContextSegment = segments.find((item) => item.startsWith('rule_context='));
    if (ruleContextSegment) {
        summary.ruleContext = ruleContextSegment.slice('rule_context='.length).trim();
    }

    const msbContextSegment = segments.find((item) => item.startsWith('msb_context='));
    if (msbContextSegment) {
        summary.msbContext = msbContextSegment.slice('msb_context='.length).trim();
    }

    const explicitValidationResultsSegment = segments.find((item) => item.startsWith('validation_results='));
    if (explicitValidationResultsSegment) {
        summary.explicitValidationResults = explicitValidationResultsSegment
            .slice('validation_results='.length)
            .trim();
    }

    const modelSegment = segments.find((item) => item.startsWith('model='));
    if (modelSegment) {
        const modelText = modelSegment.slice('model='.length).replace(/\s+/g, ' ').trim();
        summary.rawModelSummaryFull = modelText;
        summary.rawModelSummary = modelText.length > 320 ? `${modelText.slice(0, 320)}...` : modelText;
        summary.modelReason = extractQuotedField(modelText, 'reason');
        summary.modelRecommendedAction = extractQuotedField(modelText, 'recommended_action');
        summary.modelRiskScore = extractQuotedField(modelText, 'risk_score');
        summary.modelConfidence = extractQuotedField(modelText, 'confidence');
        summary.actionCategory = extractQuotedField(modelText, 'action_category');
    }

    return summary;
}
